/**
 * V4.24.46 — LO Meetup + Signed LO Upload (Web) — mirrors mobile
 * `loan_lo_meetup` screen for Type C Stages 5–6 (lo_meetup, lo_signed).
 *
 * Backend:
 *   GET  /api/public/v1/loan-cases/:id       — returns accepted offer + status
 *   POST /api/public/v1/misc/upload          — upload signed LO PDF → file_id
 *   POST /api/public/v1/loan-cases/:id/lo-signed { signed_lo_file_ids: [file_id] }
 *
 * Deep-link path: `/loan/:id/lo-meetup`.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Loader2, Upload, FileText, MapPin, Calendar, CircleCheck } from 'lucide-react';
import { LoanPageHeader } from './_shared';

export default function LoanLoMeetup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/public/v1/loan-cases/${id}`);
      setCaseData(r.data?.data || null);
    } catch (e) { toast.error('Failed to load case'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const handleUpload = async (file) => {
    if (!file || file.type !== 'application/pdf') { toast.error('PDF only'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('folder', 'signed_lo');
      const up = await api.post('/public/v1/misc/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fileId = up.data?.data?.file_id;
      if (!fileId) throw new Error('Upload returned no file_id');
      await api.post(`/public/v1/loan-cases/${id}/lo-signed`, {
        signed_lo_file_ids: [fileId],
        notes: 'Signed at branch',
      });
      toast.success('Signed LO uploaded — case moved to LO Signed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  if (loading || !caseData) return <div className="p-6 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>;

  const acceptedOffer = (caseData.offers || []).find((o) => o.id === caseData.accepted_offer_id)
    || (caseData.offers || []).find((o) => o.id === caseData.selected_offer_id)
    || (caseData.offers || []).find((o) => o.status === 'approved');
  const isSigned = caseData.status === 'lo_signed';

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6 space-y-4" data-testid="loan-lo-meetup-page">
      <LoanPageHeader caseData={caseData} title={isSigned ? 'LO Signed' : 'LO Meetup Scheduling'} />

      {acceptedOffer && (
        <Card className="p-4 space-y-2" data-testid="lo-meetup-offer-card">
          <p className="text-xs uppercase tracking-wider text-gray-500">Approved Offer</p>
          <p className="font-bold text-gray-800 text-lg">{acceptedOffer.bank_code} @ {acceptedOffer.rate_percent}%</p>
          <p className="text-sm text-gray-600">
            RM {Number(acceptedOffer.loan_amount).toLocaleString('en-MY')} · {acceptedOffer.tenure_years} years
          </p>
          {acceptedOffer.banker_name && <p className="text-xs text-gray-500">Banker: {acceptedOffer.banker_name}</p>}
        </Card>
      )}

      {!isSigned && (
        <Card className="p-4 space-y-3 bg-indigo-50 border-indigo-200" data-testid="lo-meetup-instructions">
          <p className="font-semibold text-indigo-900 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Schedule your LO meetup
          </p>
          <ul className="text-sm text-indigo-900 space-y-1 pl-6 list-disc">
            <li>Contact the banker above to arrange a branch appointment.</li>
            <li>Bring your IC and any documents the banker requested.</li>
            <li>Sign the physical Letter of Offer at the meeting.</li>
            <li>Return here and upload the signed LO as a PDF below.</li>
          </ul>
          <p className="text-xs text-indigo-800 flex items-center gap-1 mt-2">
            <MapPin className="w-3 h-3" /> The banker will confirm the branch address by phone.
          </p>
        </Card>
      )}

      <Card className="p-4 space-y-3" data-testid="lo-signed-upload-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="font-semibold text-gray-800 flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-600" />
              Signed Letter of Offer (PDF)
              {isSigned && (
                <span className="inline-flex items-center gap-1 text-emerald-700 text-xs">
                  <CircleCheck className="w-4 h-4" /> Submitted
                </span>
              )}
            </p>
            <p className="text-xs text-gray-500">PDF only. Uploading the signed LO advances your case to <b>LO Signed</b>.</p>
          </div>
          {!isSigned && (
            <>
              <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => handleUpload(e.target.files?.[0])} data-testid="lo-signed-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="lo-signed-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                Upload Signed LO
              </Button>
            </>
          )}
        </div>
      </Card>

      {isSigned && (
        <Card className="p-4 bg-emerald-50 border-emerald-200 flex items-start gap-3" data-testid="lo-signed-banner">
          <CircleCheck className="w-6 h-6 text-emerald-600 flex-shrink-0" />
          <div>
            <p className="font-semibold text-emerald-800">Loan Offer signed successfully</p>
            <p className="text-sm text-emerald-700 mt-0.5">The bank will now process your drawdown. You&apos;ll be notified once approved.</p>
          </div>
        </Card>
      )}
    </div>
  );
}
